import { Cart } from '@diamond/shared/types';
import { groupCartByDivision } from '@diamond/sol-buyer/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getCartData } from '../queries/cart';
import { useAuthStore, useShipToStore } from '../stores';

export interface UseCartDataFetchArgs {
  warehouseId?: string;
  searchQuery?: string;
}

export interface UseCartDataFetchReturn {
  cartData: Cart[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => Promise<unknown>;
  itemsWithStock: Cart[];
  groupByDivision: Record<string, Cart[]>;
  noStockProductId?: string;
}

export const useCartDataFetch = ({
  warehouseId,
  searchQuery,
}: UseCartDataFetchArgs): UseCartDataFetchReturn => {
  const token = useAuthStore((state) => state.access_token);
  const selectedShipToId = useShipToStore((state) => state.selectedShipToId);

  // FAKE DATA - Comment out real API call for local development
  const {
    data: cartData = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['Cart', selectedShipToId, warehouseId, searchQuery],
    queryFn: async () => {
      return getCartData(token, warehouseId || selectedShipToId, searchQuery);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: Boolean(token && (warehouseId || selectedShipToId)),
  });

  const itemsWithStock = useMemo(
    () =>
      cartData.filter(
        (item: Cart) => item.product.stock_status !== 'OUT_OF_STOCK'
      ),
    [cartData]
  );

  const groupByDivision = useMemo(
    () => groupCartByDivision(cartData),
    [cartData]
  );

  const noStockProductId = useMemo(() => {
    const divisions = Object.keys(groupByDivision);
    for (let i = divisions.length - 1; i >= 0; i--) {
      const division = divisions[i];
      const items = groupByDivision[division];
      for (let j = items.length - 1; j >= 0; j--) {
        if (items[j].product.stock_status === 'OUT_OF_STOCK')
          return items[j].id;
      }
    }
    return undefined;
  }, [groupByDivision]);

  return {
    cartData,
    isLoading,
    isFetching,
    refetch,
    itemsWithStock,
    groupByDivision,
    noStockProductId,
  };
};
